import { Routes } from '@angular/router';
import { Notfound } from './app/modules/common/notfound/notfound';
import { AppLayout } from './app/shared/layout/dashboard-layout/component/app.layout';
import { authGuard } from './app/core/guards/auth.guard';
import { permissionGuard } from './app/core/guards/permission.guard';

export const appRoutes: Routes = [
    {
        path: '',
        loadChildren: () => import('./app/modules/auth/auth-routing.module').then((m) => m.AuthRoutingModule)
    },

    // ── Tenant management ────────────────────────────────────────────────────
    {
        path: 'tenant',
        loadChildren: () => import('./app/modules/core-system-component/tenant-management/tenant/tenant-routing.module').then((m) => m.TenantRoutingModule)
    },
    {
        path: 'employee',
        loadChildren: () => import('./app/modules/core-system-component/tenant-management/employee/employee-routing.module').then((m) => m.EmployeeRoutingModule)
    },
    {
        path: 'payment',
        loadChildren: () => import('./app/modules/core-system-component/tenant-management/payment/payment-routing.module').then((m) => m.PaymentRoutingModule)
    },
    {
        path: 'disposition',
        loadChildren: () => import('./app/modules/core-system-component/tenant-management/disposition-management/disposition-management-routing.module').then((m) => m.DispositionManagementRoutingModule)
    },

    // ── User management ──────────────────────────────────────────────────────
    {
        path: 'role',
        loadChildren: () => import('./app/modules/core-system-component/user-management/role/role-routing.module').then((m) => m.RoleRoutingModule)
    },
    {
        path: 'user',
        loadChildren: () => import('./app/modules/core-system-component/user-management/user/user-routing.module').then((m) => m.UserRoutingModule)
    },
    {
        path: 'site-project-permissions',
        loadChildren: () => import('./app/modules/core-system-component/site-project-permissions/site-project-permissions-routing.module').then((m) => m.SiteProjectPermissionsRoutingModule)
    },

    // ── Common setup ─────────────────────────────────────────────────────────
    {
        path: 'activity-log',
        loadChildren: () => import('./app/modules/core-system-component/activity-log/activity-log-routing.module').then((m) => m.ActivityLogRoutingModule)
    },
    {
        path: 'approval-workflow',
        loadChildren: () => import('./app/modules/core-system-component/approval-workflow-management/approval-workflow-routing.module').then((m) => m.ApprovalWorkflowRoutingModule)
    },
    // ── Configuration ────────────────────────────────────────────────────────
    {
        path: 'configuration',
        loadChildren: () => import('./app/modules/kpi/configuration/configuration-routing.module').then((m) => m.ConfigurationRoutingModule)
    },
    // ── Operations Workflow ──────────────────────────────────────────────────
    {
        path: 'operations',
        loadChildren: () => import('./app/modules/kpi/operations/operations-routing.module').then((m) => m.OperationsRoutingModule)
    },
    // ── My Day (employee self-service) ───────────────────────────────────────
    {
        path: 'my-day',
        loadChildren: () => import('./app/modules/kpi/operations/my-day/my-day-routing.module').then((m) => m.MyDayRoutingModule)
    },
    // ── My Entries (employee self-service — historical) ──────────────────────
    {
        path: 'my-entries',
        loadChildren: () => import('./app/modules/kpi/operations/my-entries/my-entries-routing.module').then((m) => m.MyEntriesRoutingModule)
    },
    // ── Employee Dashboard (employee self-service KPI overview) ──────────────
    {
        path: 'employee-dashboard',
        loadChildren: () => import('./app/modules/kpi/operations/my-kpi/my-kpi-routing.module').then((m) => m.MyKpiRoutingModule)
    },
    // ── Manager Dashboard (manager self-service team overview) ───────────────
    {
        path: 'manager-dashboard',
        loadChildren: () => import('./app/modules/kpi/operations/manager-overview/manager-overview-routing.module').then((m) => m.ManagerOverviewRoutingModule)
    },
    // ── Approval Queue (manager review) ──────────────────────────────────────
    {
        path: 'approval-queue',
        loadChildren: () => import('./app/modules/kpi/operations/approval-queue/approval-queue-routing.module').then((m) => m.ApprovalQueueRoutingModule)
    },
    // ── Review Requests (manager re-review of Reject/Reinput entries) ────────
    {
        path: 'review-requests',
        loadChildren: () => import('./app/modules/kpi/operations/review-requests/review-requests-routing.module').then((m) => m.ReviewRequestsRoutingModule)
    },
    // ── My Workers (manager's scoped worker list + profile) ──────────────────
    {
        path: 'my-workers',
        loadChildren: () => import('./app/modules/kpi/operations/my-workers/my-workers-routing.module').then((m) => m.MyWorkersRoutingModule)
    },
    // ── Reports ──────────────────────────────────────────────────────────────
    {
        path: 'reports',
        loadChildren: () => import('./app/modules/kpi/reports/reports-routing.module').then((m) => m.ReportsRoutingModule)
    },
    // ── Settings ─────────────────────────────────────────────────────────────
    {
        path: 'sitemap',
        loadChildren: () => import('./app/modules/settings/sitemap/sitemap-routing.module').then((m) => m.SitemapRoutingModule)
    },
    {
        path: 'variable',
        loadChildren: () => import('./app/modules/settings/variable/variable-routing.module').then((m) => m.VariableRoutingModule)
    },
    {
        path: 'variable-type',
        loadChildren: () => import('./app/modules/settings/variable-type/variable-type-routing.module').then((m) => m.VariableTypeRoutingModule)
    },
    {
        path: 'subscription',
        loadChildren: () => import('./app/modules/settings/subscription-management/subscription-management-routing.module').then((m) => m.SubscriptionManagementRoutingModule)
    },

    // ── Notifications ─────────────────────────────────────────────────────────
    {
        path: 'notifications',
        component: AppLayout,
        children: [
            {
                path: '',
                loadComponent: () => import('./app/shared/components/notifications-page/notifications-page.component').then((m) => m.NotificationsPageComponent),
                canActivate: [authGuard],
                data: { breadcrumb: 'Notifications' }
            }
        ]
    },

    // ── My Approvals ─────────────────────────────────────────────────────────
    {
        path: 'my-approvals',
        component: AppLayout,
        children: [
            {
                path: '',
                loadComponent: () => import('./app/shared/components/my-approvals/my-approvals.component').then((m) => m.MyApprovalsComponent),
                canActivate: [authGuard, permissionGuard('/my-approvals')],
                data: { breadcrumb: 'My Approvals' }
            }
        ]
    },

    // ── Billing / subscription (reachable while trial or subscription is expired) ─
    {
        path: 'billing',
        component: AppLayout,
        children: [
            {
                path: '',
                loadComponent: () => import('./app/modules/core-system-component/tenant-management/billing/billing.component').then((m) => m.BillingComponent),
                canActivate: [authGuard],
                data: { breadcrumb: 'Billing' }
            }
        ]
    },

    // ── Fallback landing page for roles with no dashboard page configured ─────
    {
        path: 'welcome',
        component: AppLayout,
        children: [
            {
                path: '',
                loadComponent: () => import('./app/shared/components/no-dashboard-assigned/no-dashboard-assigned.component').then((m) => m.NoDashboardAssignedComponent),
                canActivate: [authGuard],
                data: { breadcrumb: 'Welcome' }
            }
        ]
    },

    { path: 'notfound', component: Notfound },
    { path: '**', redirectTo: '/notfound' }
];
